import Modal from "components/Modal";
import { FormProvider } from "react-hook-form";
import { DropdownFormProvider } from "components/DropdownFormProvider";
import Divider from "components/Divider";
import { Headline5 } from "components/typography/Headline5";
import { CheckboxFormProvider } from "components/CheckboxFormProvider";
import { UseAddUppdragFromFacilityPageReturnType } from "./useAddUppdragFromFacilityPage";
import { ComboBox } from "components/ComboBox";
import { TextInputFormProvider } from "components/TextInputFormProvider";
import { RadioCardGroup } from "components/RadioCardGroup";
import { ModalLabel } from "components/ModalLabel";
import { DatePickerNew } from "components/DatePickerNew";
import { useMemo } from "react";
import {
  UppdragScheduleDateType,
  uppdragScheduledDateAsString,
} from "types/uppdrag.type";
import { TextAreaFormProvider } from "components/TextAreaFormProvider";
import { MostUsedWasteCodePills } from "./MostUsedWasteCodePills";
import { Info } from "components/Info";
import { Tips } from "components/Tips";
import {
  AvskiljareTomningType,
  getAvskiljareTomningTypeLabel,
} from "types/enums";
import PrimaryButton from "components/buttons/PrimaryButton";
import { useCreateOrUpdateSeparatorLogic } from "hooks/useCreateOrUpdateSeparatorLogic.hook";
import { AddSeparatorModal } from "pages/app/facility/AddSeparatorModal";
import { useQueryClient } from "@tanstack/react-query";
import { FacilityQueryKey } from "api/facility.api";
import { useIsFeatureFlagEnabled } from "hooks/useFeatureFlag";
import { FeatureFlagName } from "api/featureFlag.api";

type Props = {
  logic: UseAddUppdragFromFacilityPageReturnType;
};

export const AddUppdragFromFacilityPageModal = ({ logic }: Props) => {
  const isWasteStationNotificationEnabled = useIsFeatureFlagEnabled(
    FeatureFlagName.IsWasteStationNotificationEnabled,
  );
  const selectedSeparatorUuid = logic.form.watch("separatorUuid");
  const isTomningSelected = logic.form.watch("uppgifter.tomning");
  const selectedWasteCode = logic.form.watch("selectedWasteCode");
  const scheduleDateType = logic.form.watch("scheduleDateType");
  const scheduleDate = logic.form.watch("scheduleDate");

  const separatorDropdownItems = logic.getSeparatorDropdownItems();
  const facilitySeparators = logic.facilitySeparatorList();
  const selectedSeparator = facilitySeparators.find(
    (s) => s.uuid === selectedSeparatorUuid,
  );

  const queryClient = useQueryClient();
  const addSeparatorLogic = useCreateOrUpdateSeparatorLogic(
    logic.facility,
    async (s) => {
      console.log(s);
      // Invalidate and wait for the query to refetch so the new separator is in the list
      await queryClient.invalidateQueries({
        queryKey: [FacilityQueryKey.GetOne],
      });
      // Small delay to ensure the component has re-rendered with new data
      await new Promise((resolve) => setTimeout(resolve, 100));
      logic.form.setValue("separatorUuid", s.uuid);
    },
  );

  const scheduleDateLabel = useMemo(() => {
    switch (scheduleDateType) {
      case UppdragScheduleDateType.Day:
        return "Datum";
      case UppdragScheduleDateType.Week:
        return "Vecka";
      case UppdragScheduleDateType.Month:
        return "Månad";
      default:
        return "Datum";
    }
  }, [scheduleDateType]);

  const dateCustomType = useMemo(() => {
    switch (scheduleDateType) {
      case UppdragScheduleDateType.Day:
        return "day";
      case UppdragScheduleDateType.Week:
        return "week";
      case UppdragScheduleDateType.Month:
        return "month";
      default:
        return "day";
    }
  }, [scheduleDateType]);

  const modalTitle = "Nytt uppdrag";
  const submitButtonText = logic.mutation.isPending
    ? logic.isOperator
      ? "Påbörjar..."
      : "Skapar..."
    : logic.isOperator
      ? "Påbörja"
      : "Skapa uppdrag";

  return (
    <Modal
      isOpen={logic.isModalOpen}
      title={modalTitle}
      onClose={logic.closeModal}
      afterClose={logic.afterModalClose}
      isLoading={logic.mutation.isPending}
      loadingText={
        logic.isOperator ? "Påbörjar uppdrag..." : "Skapar uppdrag..."
      }
      hideButtons
      isPrettyWide={!logic.isOperator}
    >
      <FormProvider {...logic.form}>
        <form className="mt-4 pt-4" onSubmit={logic.onSubmit}>
          <div className="space-y-6">
            {/* Separator dropdown - show if separators are available */}
            <>
              <DropdownFormProvider
                items={separatorDropdownItems}
                label="Objekt"
                required={false}
                name="separatorUuid"
                onAddClick={addSeparatorLogic.openModal}
                addButtonText="Skapa nytt objekt"
                emptyText="Inga objekt"
              />
              <AddSeparatorModal logic={addSeparatorLogic} />
            </>

            {separatorDropdownItems.length === 0 && (
              <Info text="Anläggningen har inga objekt. Du kan endast skapa avfallstransport." />
            )}

            <div className="mb-6 space-y-3">
              <Headline5>Välj aktiviteter</Headline5>

              {/* Tomning can be done without separator, but not on a provtagningspunkt */}
              <div
                className={
                  logic.isWaterMeasurementPoint(selectedSeparator)
                    ? "opacity-40"
                    : ""
                }
              >
                <CheckboxFormProvider
                  name="uppgifter.tomning"
                  label="Avfallstransport"
                  disabled={logic.isWaterMeasurementPoint(selectedSeparator)}
                />
              </div>

              {/* Separator-dependent activities */}
              {selectedSeparatorUuid && (
                <>
                  <div
                    className={
                      !logic.isAvskiljare(selectedSeparator) ? "opacity-40" : ""
                    }
                  >
                    <CheckboxFormProvider
                      name="uppgifter.sixMonths"
                      label="6-månaderskontroll"
                      disabled={!logic.isAvskiljare(selectedSeparator)}
                    />
                  </div>
                  <div
                    className={
                      !logic.isAvskiljare(selectedSeparator) ? "opacity-40" : ""
                    }
                  >
                    <CheckboxFormProvider
                      name="uppgifter.fiveYears"
                      label="5-årsbesiktning"
                      disabled={!logic.isAvskiljare(selectedSeparator)}
                    />
                  </div>
                  <div
                    className={
                      !logic.isWaterMeasurementPoint(selectedSeparator)
                        ? "opacity-40"
                        : ""
                    }
                  >
                    <CheckboxFormProvider
                      name="uppgifter.waterMeasurement"
                      label="Vattenprovtagning"
                      disabled={
                        !logic.isWaterMeasurementPoint(selectedSeparator)
                      }
                    />
                  </div>
                </>
              )}

              <Tips
                id="6b326533-22fd-41e0-9edf-a0116dc93859"
                paragraphs={[
                  "Du kan lägga till uppgifter på fler objekt efter att uppdraget skapats.",
                ]}
              />
            </div>

            {/* Waste transport section - show when tomning is selected */}
            {isTomningSelected && (
              <>
                <Divider className="my-4" />
                <Headline5 className="mb-4 text-gray-500">
                  Avfallstransport
                </Headline5>

                <DropdownFormProvider
                  items={logic.wasteStationDropdownItems}
                  label="Mottagarstation"
                  required
                  name="wasteStationUuid"
                  disabled={logic.wasteStationDropdownItems.length === 0}
                  className="mb-8"
                  hideClearButton
                />
                {isWasteStationNotificationEnabled &&
                  logic.selectedWasteStation?.wantsToBeNotified && (
                    <DatePickerNew
                      name="wasteStationDeliveryDate"
                      label="Lämningsdatum"
                      placeholder="Välj leveransdatum"
                      isClearable={false}
                      required
                    />
                  )}

                {logic.isOperator &&
                  selectedSeparatorUuid &&
                  logic.isAvskiljare(selectedSeparator) && (
                    <RadioCardGroup
                      name="tomningType"
                      label="Typ av tömning (endast avskiljare)"
                      required
                      items={[
                        {
                          id: AvskiljareTomningType.Heltomning,
                          label: getAvskiljareTomningTypeLabel(
                            AvskiljareTomningType.Heltomning,
                          ),
                        },
                        {
                          id: AvskiljareTomningType.Toppsugning,
                          label: getAvskiljareTomningTypeLabel(
                            AvskiljareTomningType.Toppsugning,
                          ),
                        },
                      ]}
                      radioGroupClassName="grid grid-cols-2 gap-x-4"
                      className="mb-6"
                    />
                  )}

                <div className="space-y-2">
                  <ModalLabel required>Avfallskod</ModalLabel>
                  <MostUsedWasteCodePills
                    mostUsedWasteCodes={logic.mostUsedWasteCodes}
                    selectedWasteCode={selectedWasteCode}
                    onSelectWasteCode={logic.selectMostUsedWasteCode}
                  />

                  <ComboBox
                    items={logic.wasteCodeComboBoxItems}
                    setQuery={logic.setWasteCodeQuery}
                    selectedItem={logic.selectedWasteCode}
                    setSelectedItem={logic.setSelectedWasteCode}
                    errorMessage={
                      logic.form.formState.errors.wasteCodeUuid?.message
                    }
                    className="mb-8"
                  />
                </div>

                <TextInputFormProvider
                  label="Uppskattad vikt"
                  name="preliminaryWeight"
                  appendix="kg"
                  customSize="half"
                  className="mb-8"
                />
              </>
            )}
          </div>

          {/* Additional Information - Only for Admin users */}
          {!logic.isOperator && (
            <>
              <Divider className="my-6" />
              <div className="space-y-6">
                <DropdownFormProvider
                  items={logic.userDropdownItems}
                  label="Operatör"
                  name="assignedToUserUuid"
                  required
                  hideClearButton
                />

                <RadioCardGroup
                  name="scheduleDateType"
                  label="Datumprecision"
                  required
                  items={[
                    { id: UppdragScheduleDateType.Day, label: "Datum" },
                    { id: UppdragScheduleDateType.Week, label: "Vecka" },
                    { id: UppdragScheduleDateType.Month, label: "Månad" },
                  ]}
                  radioGroupClassName="grid grid-cols-3 gap-x-2"
                />

                <DatePickerNew
                  name="scheduleDate"
                  label={scheduleDateLabel}
                  placeholder={scheduleDateLabel}
                  customType={dateCustomType}
                  isClearable={false}
                  required
                  useCustomInput
                  customValue={uppdragScheduledDateAsString(
                    scheduleDateType || UppdragScheduleDateType.Day,
                    scheduleDate || new Date(),
                  )}
                />

                <CheckboxFormProvider
                  name="isPrioritized"
                  label="Prioriterat uppdrag"
                />

                <TextAreaFormProvider
                  label="Uppdragsbeskrivning"
                  name="description"
                  rows={3}
                />

                <TextInputFormProvider
                  label="Referens"
                  name="referenceNumber"
                />
              </div>
            </>
          )}

          {logic.isOperator && logic.company?.isInvoicingEnabled && (
            <div className="mt-6 flex gap-x-4">
              <TextInputFormProvider
                label="Uppskattad arbetstid"
                name="estimatedWorkingHours"
                appendix="timmar"
                customSize="half"
              />
              <TextInputFormProvider
                label="Uppskattad körtid"
                name="estimatedDrivingHours"
                appendix="timmar"
                customSize="half"
              />
            </div>
          )}

          <Divider className="mb-4 mt-6" />
          <div className="flex justify-between gap-x-4">
            <button
              type="button"
              onClick={logic.closeModal}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Avbryt
            </button>
            <PrimaryButton
              type="submit"
              isLoading={logic.mutation.isPending}
              disabled={logic.mutation.isPending}
              className="rounded-md bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submitButtonText}
            </PrimaryButton>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};
